import { Button, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  notificationKeys,
} from '../api/notifications-api';
import { NotificationList } from '../components/NotificationList';

/**
 * The notification centre. Mounted in BOTH chromes (`/notifications` and
 * `/portal/notifications`) from the same component — the only difference is
 * where a notification links to.
 */
export function NotificationsPage({ portal = false }: { portal?: boolean }): ReactNode {
  const { t } = useTranslation(['claims', 'common']);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const queryClient = useQueryClient();

  const listState = {
    page: 1,
    pageSize: 100,
    sort: 'createdAt',
    dir: 'desc' as const,
    ...(unreadOnly ? { filters: { unread: true } } : {}),
  };

  const query = useQuery({
    queryKey: notificationKeys.list(listState),
    queryFn: () => fetchNotifications(listState),
  });
  useLiveQuery(['notifications'], notificationKeys.all);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidate,
    onError: (error: unknown) => {
      toastError(error);
    },
  });
  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: async () => {
      await invalidate();
      toastSuccess(t('claims:notifications.allRead'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const unread = query.data?.unread ?? 0;

  return (
    <div>
      <PageHeader
        title={t('claims:notifications.title')}
        subtitle={t('claims:notifications.subtitle')}
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-ink-light">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(event) => {
                  setUnreadOnly(event.target.checked);
                }}
              />
              {t('claims:notifications.showUnreadOnly')}
            </label>
            <Button
              type="button"
              variant="secondary"
              icon={<CheckCheck />}
              disabled={unread === 0 || markAll.isPending}
              onClick={() => {
                markAll.mutate();
              }}
            >
              {t('claims:notifications.markAllRead')}
            </Button>
          </>
        }
      />

      <p className="mb-3 text-sm text-ink-muted" aria-live="polite">
        {t('claims:notifications.unread', { count: unread })}
      </p>

      <Surface variant="card" className="overflow-hidden p-0">
        <NotificationList
          portal={portal}
          notifications={query.data?.items ?? []}
          onMarkRead={(id) => {
            markRead.mutate(id);
          }}
        />
      </Surface>
    </div>
  );
}
